import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import axiosClient from '../services/axiosClient';

function getAuthHeader() {
	try {
		const str = localStorage.getItem('userDetails');
		if (!str) return {};
		const userData = JSON.parse(str) || {};
		
		// Si on est en train d'emprunter une identité, utiliser le token d'emprunt
		if (userData.isImpersonating && userData.impersonation_token) {
			return { Authorization: `Bearer ${userData.impersonation_token}` };
		}
		
		// Sinon utiliser le token normal
		const { token, tokenType } = userData;
		if (token) return { Authorization: `${tokenType || 'Bearer'} ${token}` };
		return {};
	} catch (_) {
		return {};
	}
}

const ImpersonationExitButton = ({ className = "", style = {} }) => {
	const [isImpersonating, setIsImpersonating] = useState(false);
	const [impersonatedUserName, setImpersonatedUserName] = useState('');
	const [isExiting, setIsExiting] = useState(false);
	const [showExitModal, setShowExitModal] = useState(false);

	useEffect(() => {
		checkImpersonationStatus();
	}, []);

	const checkImpersonationStatus = () => {
		try {
			const userData = JSON.parse(localStorage.getItem('userDetails') || '{}');
			if (userData.isImpersonating === true) {
				setIsImpersonating(true);
				setImpersonatedUserName(userData.impersonated_user?.name || 'Utilisateur');
			} else {
				setIsImpersonating(false);
			}
		} catch {
			setIsImpersonating(false);
		}
	};

	const handleExitImpersonation = () => {
		setShowExitModal(true);
	};

	const confirmExit = async () => {
		try {
			setIsExiting(true);
			const response = await axiosClient.post('/admin/impersonate/leave', {}, {
				headers: { 'Accept': 'application/json', ...getAuthHeader() },
			});
			
			if (response.data.success) {
				// Restaurer les informations de l'admin original
				const originalAdminData = {
					...response.data.result.returned_to_admin,
					token: response.data.result.new_admin_token,
					tokenType: 'Bearer',
					isImpersonating: false
				};
				
				localStorage.setItem('userDetails', JSON.stringify(originalAdminData));
				
				// Rediriger vers le dashboard admin
				window.location.href = '/admin/dashboard';
			}
		} catch (err) {
			console.error('Error stopping impersonation:', err);
			const errorMessage = err.response?.data?.message || 'Erreur lors de l\'arrêt de l\'emprunt';
			alert(errorMessage);
		} finally {
			setIsExiting(false);
			setShowExitModal(false);
		}
	};

	const cancelExit = () => {
		setShowExitModal(false);
	};

	// Ne pas afficher le bouton si on n'est pas en mode emprunt
	if (!isImpersonating) {
		return null;
	}

	return (
		<div className={`alert alert-warning d-flex justify-content-between align-items-center ${className}`} style={{ ...style }}>
			<div>
				<i className="fas fa-user-secret me-2"></i>
				<strong>Mode emprunt actif</strong> - Connecté en tant que <strong>{impersonatedUserName}</strong>
			</div>
			<button
				type="button"
				className="btn btn-outline-danger btn-sm"
				onClick={handleExitImpersonation}
				disabled={isExiting}
			>
				{isExiting ? (
					<>
						<span className="spinner-border spinner-border-sm me-2" role="status"></span>
						Sortie...
					</>
				) : (
					<>
						<i className="fas fa-sign-out-alt me-1"></i>
						Quitter l'emprunt
					</>
				)}
			</button>

			{/* Modal de confirmation de sortie d'emprunt */}
			<Modal 
				show={showExitModal} 
				onHide={cancelExit}
				centered
				size="sm"
			>
				<Modal.Body className="text-center py-4">
					<div className="mb-3">
						<div 
							className="mx-auto mb-3"
							style={{
								width: '60px',
								height: '60px',
								borderRadius: '50%',
								border: '3px solid #dc3545',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								backgroundColor: '#f8d7da'
							}}
						>
							<i className="fas fa-sign-out-alt" style={{ fontSize: '24px', color: '#dc3545' }}></i>
						</div>
					</div>
					
					<h5 className="mb-3" style={{ fontWeight: '600', color: '#333' }}>
						Quitter l'emprunt d'identité ?
					</h5>
					
					<p className="text-muted mb-4" style={{ fontSize: '14px' }}>
						Voulez-vous arrêter l'emprunt d'identité de <strong>{impersonatedUserName}</strong> ?<br/>
						<small className="text-info">Vous serez redirigé vers le dashboard administrateur.</small>
					</p>
					
					<div className="d-flex justify-content-center gap-3">
						<button
							type="button"
							className="btn btn-secondary px-4"
							onClick={cancelExit}
							disabled={isExiting}
							style={{ 
								borderRadius: '6px',
								fontWeight: '500'
							}}
						>
							Annuler
						</button>
						<button
							type="button"
							className="btn btn-danger px-4"
							onClick={confirmExit}
							disabled={isExiting}
							style={{ 
								borderRadius: '6px',
								fontWeight: '500'
							}}
						>
							{isExiting ? (
								<>
									<span className="spinner-border spinner-border-sm me-2" role="status"></span>
									Sortie...
								</>
							) : (
								<>
									<i className="fas fa-sign-out-alt me-2"></i>
									Quitter
								</>
							)}
						</button>
					</div>
				</Modal.Body>
			</Modal>
		</div>
	);
};

export default ImpersonationExitButton;
